import { useState, useEffect } from 'react';
import { LineChartCard } from '@/components/charts';
import CaseViewModal, { type CaseRow } from '@/components/CaseViewModal';
import StatsCard from '@/components/StatsCard';
import StatusBadge from '@/components/StatusBadge';
import ViewButton from '@/components/ViewButton';
import { DataTable } from '@podus/ui';
import type { Column } from '@podus/ui';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import { useYear } from '@/context/YearContext';
import type { CaseManagerDashboardStats, ChartDataPoint, Case } from '@/types';

/* ─── Helpers ─── */

function mapToRow(c: Case, index: number): CaseRow {
    const applicant = c.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
    return {
        id: c.id,
        sn: index + 1,
        date: new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        complainantName: applicant?.name ?? c.caseNumber,
        caseCategory: c.caseCategory,
        disputeCategory: c.disputeCategory,
        evidence: c.evidence?.length ?? 'Nil',
        amountPaid: `₦${Number(c.amountPaid).toLocaleString()}`,
        status: c.status,
    };
}

/* ─── Component ─── */

export default function CaseManagerDashboard() {
    const { yearQuery } = useYear();
    const [selectedCase, setSelectedCase] = useState<CaseRow | null>(null);
    const [dashStats, setDashStats] = useState<CaseManagerDashboardStats | null>(null);
    const [caseVolumeChart, setCaseVolumeChart] = useState<ChartDataPoint[]>([]);
    const [recentCases, setRecentCases] = useState<CaseRow[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get<Case[]>('/api/cases/recent')
            .then((res) => setRecentCases((res.response.data ?? []).map(mapToRow)))
            .catch(() => setRecentCases([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        Promise.all([
            api.get<CaseManagerDashboardStats>(`/api/dashboard/case-manager${yearQuery}`),
            api.get<ChartDataPoint[]>(`/api/dashboard/case-manager/case-volume-chart${yearQuery}`),
        ])
            .then(([statsRes, volumeRes]) => {
                setDashStats(statsRes.response.data ?? null);
                setCaseVolumeChart(volumeRes.response.data ?? []);
            })
            .catch(() => {
                setDashStats(null);
                setCaseVolumeChart([]);
            });
    }, [yearQuery]);

    const statsCards = [
        // { label: 'Users', value: String(dashStats?.users ?? 0) },
        { label: 'All Cases', value: String(dashStats?.allCases ?? 0) },
        { label: 'Ongoing Cases', value: String(dashStats?.ongoingCases ?? 0) },
        { label: 'Resolved Cases', value: String(dashStats?.resolvedCases ?? 0) },
    ];

    const CASE_COLUMNS: Column<CaseRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'date', label: 'Date' },
        { key: 'complainantName', label: 'Complainant Name' },
        { key: 'caseCategory', label: 'Case Category' },
        { key: 'disputeCategory', label: 'Dispute Category' },
        { key: 'evidence', label: 'No of Evidence' },
        { key: 'amountPaid', label: 'Amount Paid' },
        {
            key: 'status',
            label: 'Case Status',
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => setSelectedCase(row)} />,
        },
    ];

    return (
        <div className="space-y-6  py-6">
            <div className="px-4 lg:px-8 mb-12.5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-12.5">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-secondary sm:text-3xl">Case Manager Dashboard</h1>
                            <span className="rounded-full bg-[#FFA800] px-3 py-0.5 text-[12px] font-medium text-[#4F4F4F]">
                                Version 1.0
                            </span>
                        </div>
                        <p className="mt-2 text-[14px] text-[#333333]">
                            Last updated:{' '}
                            {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {statsCards.map((s) => (
                            <StatsCard key={s.label} stat={s} />
                        ))}
                    </div>
                </div>

                <LineChartCard
                    title="Case Trend Volume"
                    data={caseVolumeChart as Record<string, unknown>[]}
                    series={[
                        { dataKey: 'newCases', stroke: '#16a34a', name: 'New Cases' },
                        { dataKey: 'pending', stroke: '#7c3aed', name: 'Pending' },
                        { dataKey: 'resolved', stroke: '#ff6600', name: 'Resolved' },
                    ]}
                />
            </div>

            <DataTable
                title="Recent Cases"
                columns={CASE_COLUMNS}
                data={recentCases}
                getRowKey={(row) => row.id}
                onExportCSV={() => exportCSV('recent-cases', CASE_COLUMNS, recentCases)}
            />

            <CaseViewModal open={!!selectedCase} onClose={() => setSelectedCase(null)} caseData={selectedCase} />
        </div>
    );
}
